import { useQuery } from "@tanstack/react-query";
import apiClient from "./apiClient";
import { useAppContext } from "../libs/appContextLib";
import { getRecipesApiUrl } from "./recipesApiHelpers";

/**
 * Fetch recent executions across all recipes
 *
 * @param {Object} options - Optional filter parameters
 * @param {string} [options.status] - Filter by status: 'completed' | 'failed' | 'running'
 * @param {number} [options.limit] - Max results (default: 10)
 * @param {boolean} [options.enabled] - Enable/disable the query (default: true)
 * @param {number} [options.staleTime] - Query stale time in ms
 * @param {number} [options.gcTime] - Query cache gc time in ms
 * @param {boolean|"always"} [options.refetchOnMount] - Refetch behavior on mount
 * @param {boolean} [options.refetchOnWindowFocus] - Refetch behavior on window focus
 * @param {boolean} [options.refetchOnReconnect] - Refetch behavior on reconnect
 * @param {boolean|number|Function} [options.retry] - Retry behavior
 * @returns {Object} React Query result with executions list
 */
export function useFetchRecentExecutions({
    status = "completed",
    limit = 10,
    enabled = true,
    staleTime = 1000 * 60 * 2,
    gcTime = 1000 * 60 * 15,
    refetchOnMount = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect,
    retry,
} = {}) {
    const { currUser } = useAppContext();

    return useQuery({
        queryKey: [`${currUser?.customerId}_recent-executions`, status, limit],
        queryFn: async () => {
            const params = { limit };
            if (status) params.status = status;

            const response = await apiClient.get(
                `${getRecipesApiUrl()}/recipes/executions`,
                { params }
            );
            return response.data;
        },
        staleTime,
        gcTime,
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus,
        refetchOnMount,
        refetchOnReconnect,
        retry: retry ?? (status === "running" ? false : undefined),
        enabled: !!currUser?.customerId && enabled, // Combine both conditions
    });
}
